import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider } from './context/i18nContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CaseDetailPage from './pages/CaseDetailPage';
import ProfilePage from './pages/ProfilePage';
import AuthCallback from './pages/AuthCallback';

// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminHomePage from './pages/admin/AdminHomePage';
import UsersPage from './pages/admin/UsersPage';
import AuditLogsPage from './pages/admin/AuditLogsPage';
import InvitationsPage from './pages/admin/InvitationsPage';
import RolesPage from './pages/admin/RolesPage';
import SiteConfigPage from './pages/admin/SiteConfigPage';
import PropertiesPage from './pages/admin/PropertiesPage';
import ContractsPage from './pages/admin/ContractsPage';
import ContractsViewPage from './pages/ContractsViewPage';
import TestLabPage from './pages/admin/TestLabPage';
import BackupsPage from './pages/admin/BackupsPage';
import PartnersPage from './pages/admin/PartnersPage';

// Auth pages
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

// Styles
import './styles/App.css';

// /?lang=en → /login?lang=en redirect
const RootRedirect = () => {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang');
  return <Navigate to={lang ? `/login?lang=${lang}` : '/login'} replace />;
};

// Szinkronizálja a bejelentkezett user preferred_theme-jét a ThemeContext-tel
const ThemeSync = () => {
  const { user } = useAuth();
  const { applyServerTheme } = useTheme();
  useEffect(() => {
    if (user?.preferred_theme) applyServerTheme(user.preferred_theme);
  }, [user?.preferred_theme, applyServerTheme]);
  return null;
};

function App() {
  return (
    // ============================================
    // PROVIDER STACK
    // ============================================
    // A sorrend fontos!
    // 1. Router: routing
    // 2. I18nProvider: nyelvkezelés
    // 3. AuthProvider: autentikáció
    <Router>
      <I18nProvider>
        <AuthProvider>
          <ThemeProvider>
          {/* ============================================
              ROUTES
              ============================================ */}
          <Routes>
            {/* PUBLIC ROUTES */}
            {/* Login - nyilvános, de ha bejelentkezet: redirect dashboard */}
            <Route path="/login" element={<Login />} />
            
            {/* Register - nyilvános */}
            <Route path="/register" element={<Register />} />
            
            {/* OAuth Callback - nyilvános, de csak SSO-ból */}
            <Route path="/auth-callback" element={<AuthCallback />} />

            {/* Email verification — public (tokennel) */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Password reset — public */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password"  element={<ResetPasswordPage />} />

            {/* PROTECTED ROUTES */}
            {/* Dashboard - csak bejelentkezet felhasználók */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* SZERZŐDÉSEK (olvasás) — ügyfél a sajátját, staff mindet */}
            <Route
              path="/contracts"
              element={
                <ProtectedRoute>
                  <ContractsViewPage />
                </ProtectedRoute>
              }
            />

            {/* CASE RÉSZLETEK — minden bejelentkezett user */}
            <Route
              path="/cases/:id"
              element={
                <ProtectedRoute>
                  <CaseDetailPage />
                </ProtectedRoute>
              }
            />

            {/* PROFILE — minden bejelentkezett user */}
            <Route
              path="/profile/:id"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />

            {/* ADMIN AREA — superadmin (0) és admin (1) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><AdminHomePage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><UsersPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><AuditLogsPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/invitations"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><InvitationsPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/roles"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><RolesPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/properties"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><PropertiesPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contracts"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><ContractsPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/partners"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <AdminLayout><PartnersPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/site-config"
              element={
                <ProtectedRoute>
                  <AdminRoute superadminOnly>
                    <AdminLayout><SiteConfigPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/test-lab"
              element={
                <ProtectedRoute>
                  <AdminRoute superadminOnly>
                    <AdminLayout><TestLabPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/backups"
              element={
                <ProtectedRoute>
                  <AdminRoute superadminOnly>
                    <AdminLayout><BackupsPage /></AdminLayout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />

            {/* DEFAULT ROUTE — query string (pl. ?lang=en) átadása */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          <ThemeSync />
          </ThemeProvider>
        </AuthProvider>
      </I18nProvider>
    </Router>
  );
}

export default App;